/**
 * Sales Calendar dialogs — add task (incl. OB video call with .ics customer
 * invite), fire offer chase, Aircall dial, discuss in Slack (channel picker),
 * and snooze/reschedule with notes.
 */
import { useState, useEffect } from "react";
import {
  Loader2, Plus, Phone, Mail, MessageSquare, ClipboardCheck, Send,
  Slack, PhoneCall, AlertTriangle, Video, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { mirrorChase } from "@/lib/api";

export const TASK_TYPE_META = {
  callback:      { label: "Call back", icon: Phone, tone: "bg-blue-50 text-blue-700 border-blue-200" },
  email:         { label: "Email reminder", icon: Mail, tone: "bg-violet-50 text-violet-700 border-violet-200" },
  sms:           { label: "Text broker", icon: MessageSquare, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ob_video_call: { label: "OB video call", icon: Video, tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  other:         { label: "Task", icon: ClipboardCheck, tone: "bg-slate-50 text-slate-700 border-slate-200" },
};
export const ORDINAL = { 1: "1st", 2: "2nd", 3: "3rd", 4: "Final" };

// ───────────────────────── Snooze / reschedule ──────────────────────────────
export function ScheduleDialog({ target, me, onClose, onDone }) {
  const [dateVal, setDateVal] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setDateVal(tomorrow.toISOString().slice(0, 10));
    setNote(target.snooze_note || "");
  }, [target]);
  if (!target) return null;

  const isTask = target.kind === "task";
  const save = async () => {
    if (!dateVal) { toast.error("Pick a date"); return; }
    try {
      setBusy(true);
      if (isTask) {
        const patch = { date: dateVal };
        if (note.trim()) patch.notes = note.trim();
        await api.patch(`/sales/tasks/${target.task_id}`, patch);
      } else {
        await api.post(`/sales/todo/${target.kind}/${target.ref_id}/schedule`,
          { date: dateVal, note });
      }
      toast.success(`${target.title}: next action moved to ${dateVal}`);
      onDone?.();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scal-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-emerald-700" />
            {isTask ? "Edit due date" : "Set next action"} — {target.title}
          </DialogTitle>
          <DialogDescription>
            {isTask
              ? "Moves the task to a new day on the diary."
              : "Snoozes this item off today's To Do — it will reappear as due on the chosen day."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">New due date *</Label>
            <Input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="scal-schedule-date" />
          </div>
          <div>
            <Label className="text-xs">Note (why / what's the plan?)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Customer flying home from holiday — will do the docs Friday" data-testid="scal-schedule-note" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={busy} data-testid="scal-schedule-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── Outcome (handle & close) ─────────────────────────
export const OUTCOMES = [
  { value: "not_interested", label: "Customer not interested" },
  { value: "new_application", label: "Processed for new application" },
  { value: "no_contact", label: "No contact — killed" },
  { value: "other", label: "Other / remove from calendar" },
];

export function OutcomeDialog({ target, me, onClose, onDone }) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setOutcome(""); setNote(""); }, [target]);
  if (!target) return null;

  const save = async () => {
    if (!outcome) { toast.error("Pick an outcome"); return; }
    try {
      setBusy(true);
      await api.post(`/sales/todo/${target.kind}/${target.ref_id}/outcome`,
        { outcome, note });
      toast.success(`${target.title} handled — ${OUTCOMES.find(o => o.value === outcome)?.label}`);
      onDone?.();
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scal-outcome-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-emerald-700" />Handle — {target.title}
          </DialogTitle>
          <DialogDescription>
            Records the outcome and takes this off the To Do list. Use &ldquo;Next action&rdquo; instead if you just want to chase again later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            {OUTCOMES.map(o => (
              <label key={o.value} className={`flex items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer transition ${outcome === o.value ? "border-emerald-500 bg-emerald-50" : "hover:bg-slate-50"}`}
                data-testid={`scal-outcome-${o.value}`}>
                <input type="radio" name="scal-outcome" checked={outcome === o.value} onChange={() => setOutcome(o.value)} className="accent-emerald-700" />
                {o.label}
              </label>
            ))}
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Spoke to director — happy with cashflow, revisit in 6 months" data-testid="scal-outcome-note" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={busy || !outcome} data-testid="scal-outcome-save">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />} Save Outcome
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Chase (webhook) dialog ────────────────────────
export function ChaseOfferDialog({ deal, onClose, onDone, rejectReasons }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const nc = deal?.next_chase;

  useEffect(() => { setReason(""); }, [deal]);
  if (!deal || !nc) return null;

  const fire = async () => {
    try {
      setBusy(true);
      await mirrorChase(deal.item_id, "offer", nc.final ? reason : null);
      toast.success(nc.final
        ? `${deal.title}: FINAL chase fired — the app will be marked Offer Rejected`
        : `${deal.title}: ${ORDINAL[nc.number]} offer chase fired (email + status via Monday automation)`);
      onDone?.();
    } catch (e) {
      toast.error("Chase failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scal-chase-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {nc.final ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <Send className="w-5 h-5 text-orange-600" />}
            {ORDINAL[nc.number]} Offer Chase — {deal.title}
          </DialogTitle>
          <DialogDescription>
            {nc.final
              ? "This is the FINAL chase. The Monday automation sends the last chase email and the app is then marked OFFER REJECTED."
              : `Fires the live Monday automation: re-sends the conditional offer (offer file attached) to ${deal.contact_name || "the broker contact"} and moves the status to "${nc.stage}".`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-slate-50 border rounded p-2 space-y-0.5">
            <div><span className="text-muted-foreground">Status:</span> {deal.status}</div>
            <div><span className="text-muted-foreground">Last chased:</span> {deal.last_offer_chase || "never"}</div>
            {deal.contact_email && <div><span className="text-muted-foreground">Goes to:</span> {deal.contact_email}</div>}
          </div>
          {nc.final && (
            <div>
              <Label className="text-xs">Offer rejected reason *</Label>
              <Select value={reason || undefined} onValueChange={setReason}>
                <SelectTrigger className="h-9" data-testid="scal-reject-reason"><SelectValue placeholder="Pick a reason…" /></SelectTrigger>
                <SelectContent>
                  {rejectReasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button className={`flex-1 ${nc.final ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`}
              onClick={fire} disabled={busy || (nc.final && !reason)} data-testid="scal-chase-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {nc.final ? "Fire FINAL Chase" : "Fire Chase"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Aircall dial dialog ───────────────────────────
export function DialDialog({ deal, me, onClose }) {
  const [meta, setMeta] = useState(null);
  const [userId, setUserId] = useState("");
  const [numberId, setNumberId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!deal) return;
    setToNumber(deal.contact_phone || "");
    api.get("/sales/calendar/aircall-meta")
      .then(r => {
        setMeta(r.data);
        if (r.data.users?.length === 1) setUserId(String(r.data.users[0].id));
        if (r.data.numbers?.length === 1) setNumberId(String(r.data.numbers[0].id));
      })
      .catch(e => toast.error("Aircall unavailable: " + (e?.response?.data?.detail || e.message)));
  }, [deal]);

  if (!deal) return null;

  const dial = async () => {
    try {
      setBusy(true);
      await api.post(`/sales/calendar/click-to-dial?actor=${encodeURIComponent(me)}`, {
        to_number: toNumber.trim(), user_id: parseInt(userId, 10),
        from_number_id: parseInt(numberId, 10), deal_name: deal.title,
      });
      toast.success("Dial pushed to your Aircall app — accept it to place the call");
      onClose();
    } catch (e) {
      toast.error("Dial failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scal-dial-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneCall className="w-5 h-5 text-blue-600" />Call via Aircall — {deal.title}</DialogTitle>
          <DialogDescription>Pushes a click-to-dial to your Aircall desktop/mobile app (you must be online in Aircall).</DialogDescription>
        </DialogHeader>
        {!meta ? (
          <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading Aircall…</div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Number to call *</Label>
              <Input value={toNumber} onChange={(e) => setToNumber(e.target.value)} placeholder="+447…" data-testid="scal-dial-number" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Your Aircall seat *</Label>
                <Select value={userId || undefined} onValueChange={setUserId}>
                  <SelectTrigger className="h-9" data-testid="scal-dial-user"><SelectValue placeholder="Pick user…" /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {meta.users.map(u => <SelectItem key={u.id} value={String(u.id)} className="text-xs">{u.name}{u.available ? " 🟢" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Dial from *</Label>
                <Select value={numberId || undefined} onValueChange={setNumberId}>
                  <SelectTrigger className="h-9" data-testid="scal-dial-line"><SelectValue placeholder="Line…" /></SelectTrigger>
                  <SelectContent className="max-h-56">
                    {meta.numbers.map(n => <SelectItem key={n.id} value={String(n.id)} className="text-xs">{n.name || n.digits}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={dial}
              disabled={busy || !toNumber.trim() || !userId || !numberId} data-testid="scal-dial-go">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />} Dial
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Discuss in Slack dialog ───────────────────────
export function DiscussDialog({ deal, me, onClose }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState("");

  useEffect(() => {
    if (!deal) return;
    setNote("");
    api.get("/sales/calendar/slack-channels")
      .then(r => { setChannels(r.data.channels || []); setChannel(r.data.default || ""); })
      .catch(() => setChannels([]));
  }, [deal]);
  if (!deal) return null;

  const send = async () => {
    try {
      setBusy(true);
      const r = await api.post(`/sales/calendar/items/${deal.item_id}/discuss`, { note, channel });
      toast.success(`Posted to ${r.data.channel} for discussion`);
      onClose();
    } catch (e) {
      toast.error("Slack post failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="scal-discuss-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Slack className="w-5 h-5 text-purple-600" />Discuss in Slack — {deal.title}</DialogTitle>
          <DialogDescription>Posts the deal (with its Monday link) into the chosen Slack channel.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Channel</Label>
            <Select value={channel || undefined} onValueChange={setChannel}>
              <SelectTrigger className="h-9" data-testid="scal-discuss-channel"><SelectValue placeholder="Pick a channel…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {channels.map(c => (
                  <SelectItem key={c.id} value={c.name} className="text-xs">#{c.name}{c.is_private ? " 🔒" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">What do you want to discuss?</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Broker pushing back on the factor rate — can we flex?" data-testid="scal-discuss-note" />
          </div>
          <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={send} disabled={busy || !channel} data-testid="scal-discuss-send">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Post to Slack
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── Add task dialog ───────────────────────────────
export function AddTaskDialog({ open, onClose, me, prefill, defaultDate, deals, users, onCreated }) {
  const blank = { title: "", task_type: "callback", date: defaultDate, time: "",
    deal_item_id: "", deal_name: "", sf_number: "", contact_name: "",
    contact_phone: "", contact_email: "", notes: "", assigned_to: "", assigned_to_name: "",
    duration_mins: 30, video_link: "", invite_customer: false };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...blank, date: defaultDate, ...(prefill || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill, defaultDate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isVideo = form.task_type === "ob_video_call";
  const pickDeal = (itemId) => {
    if (itemId === "__none__") { set("deal_item_id", ""); set("deal_name", ""); set("sf_number", ""); return; }
    const d = deals.find(x => x.item_id === itemId);
    if (d) setForm(f => ({ ...f, deal_item_id: d.item_id, deal_name: d.title, sf_number: d.sf_number,
      contact_name: f.contact_name || d.contact_name, contact_email: f.contact_email || d.contact_email }));
  };
  const pickAssignee = (email) => {
    if (email === "__none__") { set("assigned_to", ""); set("assigned_to_name", ""); return; }
    const u = users.find(x => x.email === email);
    setForm(f => ({ ...f, assigned_to: email, assigned_to_name: u?.name || email }));
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Give the task a title"); return; }
    if (!form.date) { toast.error("Pick a date"); return; }
    if (isVideo && form.invite_customer && (!form.contact_email.trim() || !form.time)) {
      toast.error("Customer email + a time are needed to send the calendar invite"); return;
    }
    try {
      setSaving(true);
      const r = await api.post(`/sales/tasks`,
        { ...form, duration_mins: parseInt(form.duration_mins, 10) || 30 });
      if (r.data.invite_error) {
        toast.warning("Task saved but the invite email FAILED: " + r.data.invite_error);
      } else if (r.data.task?.invite_sent) {
        toast.success(`Invite emailed to ${form.contact_email} from applications@ — task added`);
      } else {
        toast.success("Task added to the calendar");
      }
      onCreated?.();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="scal-add-task-dialog">
        <DialogHeader>
          <DialogTitle>Add Sales Task</DialogTitle>
          <DialogDescription>Call backs, email reminders, OB video calls and broker texts — shown on the calendar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.task_type} onValueChange={(v) => set("task_type", v)}>
                <SelectTrigger className="h-9" data-testid="scal-task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TASK_TYPE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Link to deal</Label>
              <Select value={form.deal_item_id || "__none__"} onValueChange={pickDeal}>
                <SelectTrigger className="h-9" data-testid="scal-task-deal"><SelectValue placeholder="No deal" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__none__">No deal</SelectItem>
                  {deals.map(d => <SelectItem key={d.item_id} value={d.item_id} className="text-xs">{d.title}{d.sf_number ? ` (${d.sf_number})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Call John back re offer terms" data-testid="scal-task-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="scal-task-date" />
            </div>
            <div>
              <Label className="text-xs">Time{isVideo && form.invite_customer ? " *" : ""}</Label>
              <Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="scal-task-time" />
            </div>
            <div>
              <Label className="text-xs">Assign to</Label>
              <Select value={form.assigned_to || "__none__"} onValueChange={pickAssignee}>
                <SelectTrigger className="h-9" data-testid="scal-task-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {users.map(u => <SelectItem key={u.email} value={u.email} className="text-xs">{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isVideo && (
            <div className="border rounded-md p-3 bg-indigo-50/50 space-y-3" data-testid="scal-video-panel">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Duration (mins)</Label>
                  <Input type="number" min={5} max={240} value={form.duration_mins}
                    onChange={(e) => set("duration_mins", e.target.value)} data-testid="scal-task-duration" />
                </div>
                <div>
                  <Label className="text-xs">Video link (Teams/Zoom/Meet)</Label>
                  <Input value={form.video_link} onChange={(e) => set("video_link", e.target.value)} placeholder="https://…" data-testid="scal-task-video-link" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="scal-invite-customer">
                <Checkbox checked={form.invite_customer} onCheckedChange={(v) => set("invite_customer", !!v)} />
                <span>Invite customer — emails a calendar invite (.ics) from <b>applications@simplyfunded.co.uk</b></span>
              </label>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Contact phone {form.task_type === "sms" && <span className="text-emerald-700">(for the text)</span>}</Label>
              <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="07…" data-testid="scal-task-phone" />
            </div>
            <div>
              <Label className="text-xs">Contact email{isVideo && form.invite_customer ? " *" : ""}</Label>
              <Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="name@…" data-testid="scal-task-email" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="What to say / chase…" />
          </div>
          <Button className="w-full bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={saving} data-testid="scal-task-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Add Task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
